import {isAnyOf} from '@reduxjs/toolkit'

import {formatDateFns} from '@shared/helpers/date'
import {isNil} from '@shared/helpers/lang'

import {getArrivalGuaranteeValidate, getDeliveryAddressFee, getValidateBranchDelivery} from '@web/actions/deliveryInfo'
import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {
    ProductType,
    ProductTypeType,
    SellerExceptionalCategory,
    SellerExceptionalCategoryType,
} from '@web/core/constants'
import {getDeliveryTag, isHopeSellerGuarantee as isHopeSellerGuaranteeByUtil} from '@web/helpers/policies'
import makeSlice from '@web/slices/orderSheetProducts/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {RawDateString, RawTimeString} from '../../apis/response/interface'

function convertDate(date: RawDateString) {
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
}

function convertTime(date: Maybe<RawTimeString>) {
    if (date) {
        return `${date.slice(0, 2)}:${date.slice(2, 4)}`
    }
    return date
}

const SellerExceptionCateogryMap: Record<SellerExceptionalCategoryType, ProductTypeType> = {
    [SellerExceptionalCategory.NONE]: ProductType.NORMAL,
    [SellerExceptionalCategory.BOOK]: ProductType.BOOK,
    [SellerExceptionalCategory.ALCOHOL]: ProductType.ALCOHOL,
    [SellerExceptionalCategory.TRADITIONAL_ALCOHOL]: ProductType.TRADITIONAL_ALCOHOL,
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByStorage.fulfilled, (state, {payload}) => {
                const {data} = payload

                return state?.map((product, index) => {
                    const isHopeSellerGuarantee = isHopeSellerGuaranteeByUtil(
                        product.deliveryInfo.deliveryAttributeType,
                    )
                    if (!isHopeSellerGuarantee) {
                        return product
                    }

                    const {arrivalGuaranteeDateTime, outboundLocationId, rmid, deliveryInfo} =
                        data.orderSheetProducts?.[index] ?? {}
                    return {
                        ...product,
                        arrivalGuaranteeDateTime: arrivalGuaranteeDateTime ?? null,
                        outboundLocationId: outboundLocationId ?? null,
                        rmid: rmid ?? null,
                        deliveryInfo: {
                            ...product.deliveryInfo,
                            hopeDelivery: deliveryInfo?.hopeDelivery ?? null,
                        },
                    }
                })
            })
            .addCase(initOrderSheetByAPI.fulfilled, (_, {payload}) => {
                const {
                    data: {products},
                } = payload
                return products.map((product) => {
                    return {
                        originalId: product.originalProductNo ?? '',
                        id: product.orderSheetProductId,
                        name: product.productName,
                        url: product.productUrl,
                        imageUrl: product.productImageUrl,
                        type: SellerExceptionCateogryMap[
                            product.sellerExceptionalCategoryType ?? SellerExceptionalCategory.NONE
                        ],
                        unitPrice: product.salePrice,
                        naverBenefit: product.naverBenefit,
                        customOrderDetailType: product.customOrderDetailType,
                        preOrder: product.preOrder,
                        merchantInfo: {
                            merchantProductId: product.merchantProductId,
                            merchantNo: product.merchantNo,
                            merchantChannelName: product.merchantChannelName,
                            merchantChannelServiceType: product.merchantChannelServiceType,
                        },
                        deliveryInfo: {
                            deliveryGroupKey: product.deliveryGroupKey,
                            deliveryAttributeType: product.deliveryAttributeType,
                            deliveryTagType: getDeliveryTag({
                                arrivalDate: product.arrivalGuarantee?.arrivalGuaranteeDateTime,
                                deliveryAttributeType: product.deliveryAttributeType ?? undefined,
                                deliveryTagType: product.arrivalGuarantee?.deliveryTagType ?? undefined,
                                orderDate: product.arrivalGuarantee?.arrivalGuaranteeCheckedDateTime,
                            }),
                            overseasDelivery: product.overseasDelivery,
                            branch: product.branchDeliveryView
                                ? {
                                      branchDeliveryType: product.branchDeliveryView.branchDeliveryType ?? undefined,
                                      deliverySlotId: product.branchDeliveryView.deliverySlotId,
                                      branchDeliveryDate: convertDate(product.branchDeliveryView.branchDeliveryDate),
                                      branchDeliveryStartTime: convertTime(
                                          product.branchDeliveryView.branchDeliveryStartTime,
                                      ),
                                      branchDeliveryEndTime: convertTime(
                                          product.branchDeliveryView.branchDeliveryEndTime,
                                      ),
                                      deliverySlotUsed: product.branchDeliveryView.deliverySlotUsed,
                                      branchDeliveryPreparationMinutes:
                                          product.branchDeliveryView.branchDeliveryPreparationMinutes ?? 0,
                                      branchDeliveryDurationMinutes:
                                          product.branchDeliveryView.branchDeliveryDurationMinutes ?? 0,
                                      deliveryScheduleType:
                                          product.branchDeliveryView.deliveryScheduleType ?? undefined,
                                      orderDeliveryTagType:
                                          product.branchDeliveryView.orderDeliveryTagType ?? undefined,
                                  }
                                : null,
                            todayDispatch:
                                !isNil(product.todayDispatch) &&
                                (product.todayDispatchBasisTime ||
                                    product.todayDispatchExpectedDateTime ||
                                    product.todayDispatchNotAppliedReason)
                                    ? {
                                          shouldTodayDispatch: product.todayDispatch,
                                          basisTime: convertTime(product.todayDispatchBasisTime),
                                          expectedDate: product.todayDispatchExpectedDateTime,
                                          notAppliedReason: product.todayDispatchNotAppliedReason,
                                      }
                                    : null,
                            hopeDelivery: product.hopeDelivery
                                ? {
                                      region: product.hopeDelivery.region,
                                      additionalFee: product.hopeDelivery.additionalFee ?? 0,
                                      hopeDeliveryDate: product.hopeDelivery.hopeDeliveryDate,
                                      hopeDeliveryTime: product.hopeDelivery.hopeDeliveryTime
                                          ? `${product.hopeDelivery.hopeDeliveryTime.slice(0, 5)}`
                                          : null,
                                  }
                                : null,
                            deliveryPrediction: product.deliveryPrediction && {
                                ...product.deliveryPrediction,
                                best: true,
                                visible: true,
                            },
                        },
                        items: product.items.map((item) => {
                            return {
                                orderSheetItemId: item.orderSheetItemId,
                                productClassType: item.productClassType,
                                itemNo: item.itemNo,
                                price: item.price,
                                quantity: item.quantity,
                                orderAmount: item.orderAmount,
                                optionContents: item.optionContents,
                                branchId: item.branchId,
                                branchBenefitType: item.branchBenefitType,
                                branchBenefitValue: item.branchBenefitValue,
                                branchBenefitApplyValue: item.benefitApplyValue,
                                storageType: item.storageType ?? undefined,
                                elements: item.elements,
                            }
                        }),
                        gift: product.gift,
                        cupDeposit: null,
                        ecouponBarcodeConnect: product.ecouponBarcodeConnect,
                        arrivalGuaranteeDateTime: product.arrivalGuarantee?.arrivalGuaranteeDateTime ?? null,
                        arrivalGuaranteeCheckedDateTime:
                            product.arrivalGuarantee?.arrivalGuaranteeCheckedDateTime ?? null,
                        rmid: product.arrivalGuarantee?.rmid ?? null,
                        outboundLocationId: product.arrivalGuarantee?.outboundLocationId ?? null,
                        installationProduct: product.installationProduct ?? null,
                        quantity: null,
                        orderAmount: null,
                    }
                })
            })
            .addCase(getValidateBranchDelivery.fulfilled, (state, {payload}) => {
                const {updatedBranchExternalDelivery} = payload
                if (updatedBranchExternalDelivery) {
                    const {deliveryScheduleType, branchDeliveryDate, branchDeliveryEndTime, orderDeliveryTagType} =
                        updatedBranchExternalDelivery

                    return state.map((product) => {
                        return {
                            ...product,
                            deliveryInfo: {
                                ...product.deliveryInfo,
                                ...(product.deliveryInfo.branch && {
                                    branch: {
                                        ...product.deliveryInfo.branch,
                                        deliveryScheduleType,
                                        branchDeliveryDate: convertDate(branchDeliveryDate),
                                        branchDeliveryEndTime: convertTime(branchDeliveryEndTime),
                                        orderDeliveryTagType,
                                    },
                                }),
                            },
                        }
                    })
                }
            })
            .addMatcher(
                isAnyOf(getDeliveryAddressFee.fulfilled, getArrivalGuaranteeValidate.fulfilled),
                (state, {payload}) => {
                    if (payload.arrivalGuaranteeInfo?.isChanged) {
                        return state.map((product) => {
                            const updatedArrivalGuarantee =
                                payload.data.updatedArrivalGuaranteesByOrderSheetProductId[product.id] ?? null
                            const {
                                arrivalGuaranteeDateTime,
                                arrivalGuaranteeCheckedDateTime,
                                rmid,
                                outboundLocationId,
                                deliveryTagType,
                            } = updatedArrivalGuarantee ?? {
                                arrivalGuaranteeDateTime: product.arrivalGuaranteeDateTime,
                                arrivalGuaranteeCheckedDateTime: product.arrivalGuaranteeCheckedDateTime,
                                rmid: product.rmid,
                                outboundLocationId: product.outboundLocationId,
                                deliveryTagType: product.deliveryInfo.deliveryTagType,
                            }
                            const hopeDeliveryDate = arrivalGuaranteeDateTime
                                ? formatDateFns(arrivalGuaranteeDateTime, 'yyyy-MM-dd')
                                : null
                            const hopeDeliveryTime = arrivalGuaranteeDateTime
                                ? formatDateFns(arrivalGuaranteeDateTime, 'HH:mm')
                                : null

                            return {
                                ...product,
                                arrivalGuaranteeDateTime,
                                arrivalGuaranteeCheckedDateTime,
                                rmid,
                                outboundLocationId,
                                deliveryInfo: {
                                    ...product.deliveryInfo,
                                    ...(product.deliveryInfo.hopeDelivery &&
                                        isHopeSellerGuaranteeByUtil(product.deliveryInfo.deliveryAttributeType) && {
                                            hopeDelivery: {
                                                ...product.deliveryInfo.hopeDelivery,
                                                hopeDeliveryDate,
                                                hopeDeliveryTime,
                                            },
                                        }),
                                    deliveryTagType: getDeliveryTag({
                                        arrivalDate: arrivalGuaranteeDateTime ?? undefined,
                                        deliveryAttributeType: product.deliveryInfo.deliveryAttributeType ?? undefined,
                                        deliveryTagType,
                                        orderDate: arrivalGuaranteeCheckedDateTime ?? undefined,
                                    }),
                                },
                            }
                        })
                    }
                    return state
                },
            )
    },
})

export default slice.reducer
